import { useState } from 'react';

import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isAfter, parse, subMonths, subYears } from 'date-fns';
import { ptBR } from 'date-fns/locale';

type PeriodDatePickerProps = {
  label?: string;
  allText?: string;
  filters: {
    dateRange: {
      start: string | null;
      end: string | null;
    };
  };
  setFilters: (filters: PeriodDatePickerProps['filters']) => void;
  disabled: boolean;
};

export function PeriodDatePicker({
  label = 'Data',
  allText = 'Todos',
  filters,
  setFilters,
  disabled,
}: PeriodDatePickerProps) {
  const getInitialPeriod = () => {
    if (!filters.dateRange.start && !filters.dateRange.end) {
      return 'All';
    }

    const now = new Date();
    const currentDate = format(now, 'yyyy-MM-dd');

    if (filters.dateRange.end === currentDate) {
      const startDate = filters.dateRange.start;
      if (startDate) {
        const oneYearAgo = format(subYears(now, 1), 'yyyy-MM-dd');
        const sixMonthsAgo = format(subMonths(now, 6), 'yyyy-MM-dd');
        const threeMonthsAgo = format(subMonths(now, 3), 'yyyy-MM-dd');
        const oneMonthAgo = format(subMonths(now, 1), 'yyyy-MM-dd');

        if (startDate === oneYearAgo) return 'M12';
        if (startDate === sixMonthsAgo) return 'M6';
        if (startDate === threeMonthsAgo) return 'M3';
        if (startDate === oneMonthAgo) return 'M1';
      }
    }

    return 'Custom';
  };

  const [selectedPeriod, setSelectedPeriod] = useState(getInitialPeriod);
  const [errors, setErrors] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});

  const validateDates = (startDate: Date | null, endDate: Date | null) => {
    const newErrors: { startDate?: string; endDate?: string } = {};

    if (startDate && endDate && isAfter(startDate, endDate)) {
      newErrors.startDate =
        'A data inicial deve ser anterior ou igual à data final';
      newErrors.endDate =
        'A data final deve ser posterior ou igual à data inicial';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartDateChange = (date: Date | null) => {
    const newFilters = {
      ...filters,
      dateRange: {
        ...filters.dateRange,
        start: date ? format(date, 'yyyy-MM-dd') : null,
      },
    };
    setFilters(newFilters);
    validateDates(
      date,
      newFilters.dateRange.end
        ? parse(newFilters.dateRange.end, 'yyyy-MM-dd', new Date())
        : null,
    );
  };

  const handleEndDateChange = (date: Date | null) => {
    const newFilters = {
      ...filters,
      dateRange: {
        ...filters.dateRange,
        end: date ? format(date, 'yyyy-MM-dd') : null,
      },
    };
    setFilters(newFilters);
    validateDates(
      newFilters.dateRange.start
        ? parse(newFilters.dateRange.start, 'yyyy-MM-dd', new Date())
        : null,
      date,
    );
  };

  const handleChangeSelectPeriod = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setSelectedPeriod(value);

    const now = new Date();

    switch (value) {
      case 'All':
        setFilters({
          ...filters,
          dateRange: {
            ...filters.dateRange,
            start: null,
            end: null,
          },
        });
        break;

      case 'M12':
        setFilters({
          ...filters,
          dateRange: {
            ...filters.dateRange,
            start: format(subYears(now, 1), 'yyyy-MM-dd'),
            end: format(now, 'yyyy-MM-dd'),
          },
        });
        break;

      case 'M6':
        setFilters({
          ...filters,
          dateRange: {
            ...filters.dateRange,
            start: format(subMonths(now, 6), 'yyyy-MM-dd'),
            end: format(now, 'yyyy-MM-dd'),
          },
        });
        break;
      case 'M3':
        setFilters({
          ...filters,
          dateRange: {
            ...filters.dateRange,
            start: format(subMonths(now, 3), 'yyyy-MM-dd'),
            end: format(now, 'yyyy-MM-dd'),
          },
        });
        break;
      case 'M1':
        setFilters({
          ...filters,
          dateRange: {
            ...filters.dateRange,
            start: format(subMonths(now, 1), 'yyyy-MM-dd'),
            end: format(now, 'yyyy-MM-dd'),
          },
        });
        break;
      default:
        break;
    }
  };

  const hasDateErrors = Object.keys(errors).length > 0;

  return (
    <Box
      display="flex"
      width="100%"
      gap={2}
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
      }}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            gap: 2,
            alignItems: 'flex-start',
          }}>
          <FormControl sx={{ minWidth: '160px', width: '100%' }} size="small">
            <InputLabel>{label}</InputLabel>
            <Select
              label={label}
              id="select-period"
              value={selectedPeriod}
              onChange={handleChangeSelectPeriod}
              disabled={disabled}>
              <MenuItem value="M12">Últimos 12 meses</MenuItem>
              <MenuItem value="M6">Últimos 6 meses</MenuItem>
              <MenuItem value="M3">Últimos 3 meses</MenuItem>
              <MenuItem value="M1">Último mês</MenuItem>
              <MenuItem value="All">{allText}</MenuItem>
              <MenuItem value="Custom">Definir período</MenuItem>
            </Select>
          </FormControl>

          {selectedPeriod === 'Custom' && (
            <Box display="flex" gap={1}>
              <FormControl
                variant="outlined"
                size="small"
                error={!!errors.startDate}
                sx={(theme) => ({
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                  },
                })}>
                <DatePicker
                  label="Data Inicial"
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.startDate,
                      helperText: errors.startDate,
                    },
                  }}
                  value={
                    filters.dateRange.start
                      ? parse(filters.dateRange.start, 'yyyy-MM-dd', new Date())
                      : null
                  }
                  onChange={handleStartDateChange}
                />
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                error={!!errors.endDate}
                sx={(theme) => ({
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                  },
                })}>
                <DatePicker
                  label="Data Final"
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.endDate,
                      helperText: errors.endDate,
                    },
                  }}
                  value={
                    filters.dateRange.end
                      ? parse(filters.dateRange.end, 'yyyy-MM-dd', new Date())
                      : null
                  }
                  onChange={handleEndDateChange}
                />
              </FormControl>
              {hasDateErrors && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Por favor, corrija as datas selecionadas. A data inicial deve
                  ser anterior ou igual à data final.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </LocalizationProvider>
    </Box>
  );
}
